import { PaperPlaneTilt, X } from '@phosphor-icons/react';
import type { MemberDto } from '@reelplicate/api-client/model';
import { useListMembers } from '@reelplicate/api-client/team';
import {
  Avatar,
  Button,
  cn,
  IconButton,
  Inline,
  Popover,
  PopoverAnchor,
  PopoverContent,
  Stack,
  TextArea,
} from '@reelplicate/ui';
import {
  type KeyboardEvent,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { fromApiRole, roleName } from '@/features/team/roles';

import {
  activeMention,
  insertMention,
  type Mentioned,
  toEditable,
  toTokens,
} from './mention-format';

const MAX_OPTIONS = 6;

const memberName = (member: MemberDto) => member.name ?? member.email ?? 'Teammate';

/**
 * The comment box (Figma "Comments / Composer" and "Mention menu"): typing @
 * opens the workspace's members above the box, filtered by what follows the
 * @; ↑/↓ and Enter (or a click) insert them, Escape closes the list. ⌘↵ sends.
 * `onSubmit` gets the body with `@[Name](id)` tokens; the box clears when it
 * resolves and keeps the text when it throws.
 */
export function MentionComposer({
  workspaceId,
  initialBody = '',
  placeholder = 'Add a comment…',
  submitLabel = 'Send',
  replyingTo,
  onCancelReply,
  onCancel,
  onSubmit,
  autoFocus,
}: {
  workspaceId: string;
  /** A token body to edit. */
  initialBody?: string;
  placeholder?: string;
  submitLabel?: string;
  /** Shows "Replying to <name> ✕". */
  replyingTo?: string | null;
  onCancelReply?: () => void;
  /** Shows a Cancel button (editing). */
  onCancel?: () => void;
  onSubmit: (body: string) => Promise<unknown>;
  autoFocus?: boolean;
}) {
  const initial = useMemo(() => toEditable(initialBody), [initialBody]);
  const [text, setText] = useState(initial.text);
  const [mentions, setMentions] = useState<Mentioned[]>(initial.mentions);
  const [caret, setCaret] = useState(0);
  const [highlight, setHighlight] = useState(0);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  const [sending, setSending] = useState(false);
  const box = useRef<HTMLTextAreaElement>(null);
  // Where the caret goes after a mention is inserted, set before the next keystroke.
  const pendingCaret = useRef<number | null>(null);
  useLayoutEffect(() => {
    const at = pendingCaret.current;
    if (at === null || !box.current) return;
    pendingCaret.current = null;
    box.current.focus();
    box.current.setSelectionRange(at, at);
  }, [text]);
  const listId = useId();
  const { data: members } = useListMembers(workspaceId, {
    query: { enabled: Boolean(workspaceId), throwOnError: false },
  });

  const mention = activeMention(text, caret);
  const matches = useMemo(() => {
    if (!mention) return [];
    const query = mention.query.toLowerCase();
    return (members ?? [])
      .filter((m) => !m.isYou)
      .filter(
        (m) =>
          memberName(m).toLowerCase().includes(query) ||
          (m.email ?? '').toLowerCase().startsWith(query),
      )
      .slice(0, MAX_OPTIONS);
  }, [members, mention]);
  const open = Boolean(mention) && matches.length > 0 && dismissedAt !== mention?.start;
  const active = Math.min(highlight, Math.max(0, matches.length - 1));

  const track = (el: HTMLTextAreaElement) => setCaret(el.selectionStart);

  const choose = (member: MemberDto) => {
    if (!mention) return;
    const name = memberName(member);
    const next = insertMention(text, mention.start, caret, name);
    setText(next.text);
    setCaret(next.caret);
    setHighlight(0);
    if (!mentions.some((m) => m.id === member.userId)) {
      setMentions([...mentions, { id: member.userId, name }]);
    }
    pendingCaret.current = next.caret;
  };

  const submit = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await onSubmit(toTokens(text.trim(), mentions));
      setText('');
      setMentions([]);
      setCaret(0);
    } catch {
      // Shown as a toast; the text stays so nothing is lost.
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (open) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        setHighlight((active + step + matches.length) % matches.length);
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        choose(matches[active]!);
        return;
      }
      if (event.key === 'Escape') {
        // Close the list, not the drawer around it.
        event.preventDefault();
        event.stopPropagation();
        setDismissedAt(mention?.start ?? null);
        return;
      }
    }
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void submit();
    }
  };

  return (
    <Stack gap={2}>
      {replyingTo && (
        <Inline gap={1} alignY="center">
          <p className="text-caption text-text-secondary">
            Replying to <span className="text-text-primary">{replyingTo}</span>
          </p>
          <IconButton
            icon={X}
            size="sm"
            variant="ghost"
            aria-label="Cancel reply"
            onClick={onCancelReply}
          />
        </Inline>
      )}
      <Popover open={open}>
        <PopoverAnchor asChild>
          <div>
            <TextArea
              ref={box}
              value={text}
              placeholder={placeholder}
              aria-label={submitLabel === 'Send' ? 'Comment' : 'Edit comment'}
              aria-controls={open ? listId : undefined}
              aria-activedescendant={open ? `${listId}-${active}` : undefined}
              aria-autocomplete="list"
              autoFocus={autoFocus}
              onChange={(event) => {
                setText(event.target.value);
                track(event.target);
                setHighlight(0);
                setDismissedAt(null);
              }}
              onSelect={(event) => track(event.currentTarget)}
              onKeyDown={onKeyDown}
            />
          </div>
        </PopoverAnchor>
        <PopoverContent
          side="top"
          align="start"
          aria-label="Mention a teammate"
          className="w-74 p-1"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          <p className="text-overline text-text-tertiary px-2 pt-1.5 pb-1 uppercase">
            Mention a teammate
          </p>
          <ul id={listId} role="listbox" aria-label="Teammates">
            {matches.map((member, index) => (
              <li
                key={member.userId}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === active}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5',
                  index === active ? 'bg-brand-subtle' : 'hover:bg-bg-subtle',
                )}
                onMouseDown={(event) => {
                  // Keep focus (and the caret) in the box.
                  event.preventDefault();
                  choose(member);
                }}
                onMouseEnter={() => setHighlight(index)}
              >
                <Avatar
                  size={24}
                  name={memberName(member)}
                  src={member.imageUrl ?? undefined}
                  alt=""
                />
                <span className="flex min-w-0 flex-col">
                  <span className="text-body-sm-strong text-text-primary truncate">
                    {memberName(member)}
                  </span>
                  <span className="text-caption text-text-tertiary truncate">
                    {roleName(fromApiRole(member.role))}
                    {member.email ? ` · ${member.email}` : ''}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
      <Inline gap={2} alignY="center">
        <p className="text-caption text-text-tertiary min-w-0 flex-1">
          Type @ to mention someone · ⌘↵ to {submitLabel === 'Send' ? 'send' : 'save'}
        </p>
        {onCancel && (
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          size="sm"
          leadingIcon={PaperPlaneTilt}
          disabled={!text.trim()}
          loading={sending}
          onClick={() => void submit()}
        >
          {submitLabel}
        </Button>
      </Inline>
    </Stack>
  );
}
